/**
 * The verified tick's geometry and colours, pure (no React), shared by the page's tick (components/VerifyTicks) and the token share card
 * drawn on the server (server/og.tsx), so both show the same seal.
 */

/** a scalloped circle: 24 points alternating between two radii, joined by quadratic curves through their midpoints (24 x 24 box) */
export const SEAL = (() => {
  const n = 24
  const pts = Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2
    const r = i % 2 === 0 ? 11 : 9.3
    return [12 + r * Math.cos(a), 12 + r * Math.sin(a)]
  })
  const mid = (p: number[], q: number[]) => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
  const f = (v: number) => v.toFixed(2)
  const start = mid(pts[n - 1], pts[0])
  let d = `M${f(start[0])} ${f(start[1])}`
  for (let i = 0; i < n; i++) {
    const m = mid(pts[i], pts[(i + 1) % n])
    d += `Q${f(pts[i][0])} ${f(pts[i][1])} ${f(m[0])} ${f(m[1])}`
  }
  return `${d}Z`
})()

/** the check inside the seal (same 24 x 24 box) */
export const SEAL_CHECK = 'M7.6 12.4 10.6 15.4 16.6 9.2'

/** info = blue (wallet proof), team = gold (CADINU team review), cad1 = green (launched with CAD1: automatic, not a review) */
export type TickKind = 'info' | 'team' | 'cad1'
export const TICK_TONES: Record<TickKind, { hi: string; mid: string; lo: string; check: string; glow: string; ring: string }> = {
  // blue: the white check keeps >= 3:1 on every part of the face
  info: { hi: '#a9dcff', mid: '#2f8af5', lo: '#1559c9', check: '#ffffff', glow: 'rgba(47, 138, 245, 0.55)', ring: 'rgba(214, 236, 255, 0.55)' },
  // gold: a dark check, white on gold would be under 2:1
  team: { hi: '#fff3c2', mid: '#f4b93a', lo: '#b97808', check: '#2a1800', glow: 'rgba(244, 185, 58, 0.5)', ring: 'rgba(255, 246, 214, 0.65)' },
  // green: "Launched with CAD1" (Bez, 2026-10-09); the white check keeps >= 3:1 on the darker half of the face
  cad1: { hi: '#b8f7cf', mid: '#1f9d55', lo: '#0c6b36', check: '#ffffff', glow: 'rgba(31, 157, 85, 0.55)', ring: 'rgba(214, 255, 228, 0.6)' },
}
